/* ++++++++++ --------------- IMPORTS --------------- ++++++++++ */
// libraries
import React, { useState, useEffect, useRef } from 'react';
import { useSelector } from 'react-redux';
// utils
import {
  formatNumber,
  getChangeRateDirection,
  getEnabledRollUps,
  formatProductAnalyticsReportDates,
  formatProductAnalyticsTableDate,
  getChartColorsSubset,
  chartZAxisColor,
} from 'utils/charts';
// material
import CardToggles from 'material/cardwrapper/cardtoggles/cardtoggles';
import CardToggle from 'material/cardwrapper/cardtoggles/cardtoggle/cardtoggle';
import DetailsTable from 'material/detailstable/detailstable';
import BarChart from 'material/barchart/barchart';
import ChartLegend from 'material/chartlegend/chartlegend';
import ChartReportSection from 'material/chartreportsection/chartreportsection';
// styles
import injectSheet from 'react-jss';
import styles from './pageviewreport_styles';
import LineChart from 'material/linechart/linechart';
import { isPercentageMetric } from '../../../helperfunction';


const GENERATED_PAGE_VIEW_TITLE = 'Page View % Uptick'
const TOTAL_NEW_PAGE_VIEWS_TITLE = 'Total Page Views'
/* ========== ~~~~~~~~~~ TRENDING NOW V2 ANALYTICS - PAGE VIEW REPORT ~~~~~~~~~~ ========== */
const PageViewReport = injectSheet(styles)((props) => {
  // props
  const { selectedInterval, setSelectedInterval } = props;

  // redux state
  const { overview, details, status } = useSelector((state) => state.trendingNowV2.pageViewReport);
  const [selectedReport, setSelectedReport] = useState('generated-page-view');
  const [isLoadingReport, loadReport] = useState(false);
  const timerRef = useRef(null);
  const { filters } = useSelector((state) => state.app);
  const changeType = filters.compareTo;
  const isGeneratedPageViewSelected = selectedReport === 'generated-page-view';

  // component states
  const [generatedPageViewLegends, setGeneratedPageViewLegends] = useState([]);
  const [totalNewPageViewsChartLegends, setTotalNewPageViewsChartLegends] = useState([]);
  const [showGeneratedPageView, setShowGeneratedPageView] = useState(false);
  const [enabledRollups, setEnabledRollups] = useState([]);
  const [selectedPeriodX, setSelectedPeriodX] = useState(null);

  // component constants
  const chartTableColors = getChartColorsSubset(2);
  const avgInViewProducts = [{ id: 'pageViewPercentage', name: GENERATED_PAGE_VIEW_TITLE, color: chartTableColors[0] }];
  const totalNewPageViewsProduct = [{ id: 'totalPageViews', name: TOTAL_NEW_PAGE_VIEWS_TITLE, color: chartTableColors[0] }];
  const totalDatesForInterval = details ? details[selectedInterval] : {};

  // when page gets loaded, then show total avgInView and totalEmbedLoad
  useEffect(() => {
    if (status === 'fulfilled' && overview) {
      showAggregateGeneratedPageView();
      showAggregateTotalNewPageViews();
    }
  }, [overview]);

  // when mouse hover leaves the chart show back the net total aggregate
  useEffect(() => {
    if (showGeneratedPageView) {
      if (isGeneratedPageViewSelected) showAggregateGeneratedPageView();
      else showAggregateTotalNewPageViews();
    }
  }, [showGeneratedPageView]);

  useEffect(() => {
    // Clear the interval when the component unmounts
    return () => clearTimeout(timerRef.current);
  }, []);

  // disable/enable intervals based on the number of dates
  useEffect(() => {
    if (status === 'fulfilled' && enabledRollups.length === 0) {
      const rollups = getEnabledRollUps(details, 'commenting');
      setEnabledRollups(rollups);
      if (rollups.length > 0) setSelectedInterval(rollups[0]);
      else setSelectedInterval('monthly');
    } else {
      setEnabledRollups([]);
      setSelectedInterval('daily');
      setGeneratedPageViewLegends([]);
      setTotalNewPageViewsChartLegends([]);
    }
  }, [status]);

  /**
   * show aggregate total for total avgInView
   */
  const showAggregateGeneratedPageView = () => {
    const productArr = avgInViewProducts.map((product) => ({
      ...product,
      value: Object.keys(overview).length > 0 ? overview.period[product.id] : 0,
      changeRate: Object.keys(overview).length > 0 ? overview.period[`${product.id}RateChange`] : 0,
      checked:
        generatedPageViewLegends.length > 0
          ? generatedPageViewLegends.filter((legend) => legend.id === product.id)[0].checked
          : true,
    }));
    setGeneratedPageViewLegends(productArr);
  };

  /**
   * show aggregate total for total embedLoad
   */
  const showAggregateTotalNewPageViews = () => {
    const productArr = totalNewPageViewsProduct.map((product) => ({
      ...product,
      value: Object.keys(overview).length > 0 ? overview.period[product.id] : 0,
      changeRate: Object.keys(overview).length > 0 ? overview.period[`${product.id}RateChange`] : 0,
      checked:
        totalNewPageViewsChartLegends.length > 0
          ? totalNewPageViewsChartLegends.filter((legend) => legend.id === product.id)[0].checked
          : true,
    }));
    setTotalNewPageViewsChartLegends(productArr);
  };

  const getBarChartKeys = () =>
    isGeneratedPageViewSelected
      ? avgInViewProducts.map((product) => product.name).reverse()
      : totalNewPageViewsProduct.map((product) => product.name).reverse();

  const getBarChartColors = () =>
    isGeneratedPageViewSelected
      ? avgInViewProducts.map((product) => product.color).reverse()
      : totalNewPageViewsProduct.map((product) => product.color).reverse();

  const renderAvgInViewLineChartLegends = () => {
    return generatedPageViewLegends.map((legend, index) => {
      return (
        <ChartLegend
          title={`${legend.name}`}
          checkboxColor={legend.color}
          checked={legend.checked}
          onCheck={(checked) => {
            const legends = [...generatedPageViewLegends];
            legends[index] = {
              ...legends[index],
              checked: checked,
            };
            setGeneratedPageViewLegends(legends);
          }}
          value={formatNumber(legend.value, 'percent')}
          changeDirection={legend.changeRate < 0 ? 'negative' : 'positive'}
          change={formatNumber(legend.changeRate ? legend.changeRate : 0, 'percent')}
          changeType={changeType}
          metricType={'commenting'}
        />
      );
    });
  };

  const renderTotalNewPageViewsBarChartLegends = () => {
    return totalNewPageViewsChartLegends.map((legend, index) => {
      return (
        <ChartLegend
          title={legend.name}
          checkboxColor={legend.color}
          checked={legend.checked}
          onCheck={(checked) => {
            const legends = [...totalNewPageViewsChartLegends];
            legends[index] = {
              ...legends[index],
              checked: checked,
            };
            setTotalNewPageViewsChartLegends(legends);
          }}
          value={formatNumber(legend.value, 'number')}
          changeDirection={legend.changeRate < 0 ? 'negative' : 'positive'}
          change={formatNumber(legend.changeRate ? legend.changeRate : 0, 'percent')}
          changeType={changeType}
          metricType={'trending'}
        />
      );
    });
  };

  const transformTotalEmbedLoadBarChartData = () => {
    let barChartData = [];
    barChartData = Object.keys(totalDatesForInterval).map((date, index) => {
      const dateLength = Object.keys(totalDatesForInterval).length;
      const formattedDate = formatProductAnalyticsReportDates(date, selectedInterval, dateLength, 10, true);
      let item = { date: formattedDate };
      totalNewPageViewsProduct.forEach((product) => {
        const detailsDateData = details[selectedInterval][date];
        const checkedProducts = totalNewPageViewsChartLegends.filter((product) => product.checked === true);
        if (checkedProducts.map((product) => product.id).indexOf(product.id) >= 0) {
          item[product.name] = detailsDateData[product.id];
          item[`${product.name}Color`] = product.color;
          item['origDate'] = date;
          item['formattedTableDate'] = formatProductAnalyticsTableDate(
            date,
            selectedInterval,
            detailsDateData.startDate,
            detailsDateData.endDate,
          );
        }
      });
      return item;
    });
    return barChartData;
  };

  const transformGeneratedPageViewLineChartData = () => {
    const lineChartData = [];
    const dateLength = Object.keys(totalDatesForInterval).length;
    if (details && details[selectedInterval]) {
      const checkedProducts = generatedPageViewLegends.filter((product) => product.checked === true);
      const items = [];
      avgInViewProducts.forEach((product) => {
        if (checkedProducts.map((product) => product.id).indexOf(product.id) >= 0) {
          Object.keys(details[selectedInterval]).forEach((date, index) => {
            const value = details[selectedInterval][date].pageViewPercentage;
            // To allow duplicate x-axis labels
            let spaces = '';
            for (let i = 0; i < index; i++) spaces += ' ';
            const detailsDateData = details[selectedInterval][date];
            items.push({
              x: formatProductAnalyticsReportDates(date, selectedInterval, dateLength, 10, true).concat(spaces),
              y: value,
              origDate: date,
              formattedDate: formatProductAnalyticsTableDate(
                date,
                selectedInterval,
                detailsDateData.startDate,
                detailsDateData.endDate,
              ),
              productName: GENERATED_PAGE_VIEW_TITLE,
            });
          });

          lineChartData.push({
            id: product.id,
            color: product.color,
            data: items,
          });
        }
      });
    }
    return lineChartData;
  };

  const handleHoverOnEmbedLoadBarChart = (barData) => {
    const date = barData.data ? barData.data.origDate : barData.origDate;
    const legends = [...totalNewPageViewsChartLegends];

    totalNewPageViewsProduct.forEach((product) => {
      let value = 0;
      let changeRate = 0;
      const productDetails = details[selectedInterval][date] ? details[selectedInterval][date] : null;
      if (productDetails) {
        value = productDetails[product.id] ? productDetails[product.id] : 0;
        changeRate = productDetails[`${product.id}RateChange`] ? productDetails[`${product.id}RateChange`] : 0;
      }

      const legendIndex = totalNewPageViewsChartLegends.map((legend) => legend.id).indexOf(product.id);
      legends[legendIndex] = {
        ...legends[legendIndex],
        value,
        changeRate,
      };
    });
    setTotalNewPageViewsChartLegends(legends);
    setSelectedPeriodX(`${barData.data ? barData.data.formattedTableDate : barData.formattedTableDate}`);
    setShowGeneratedPageView(false);
  };
  const handleHoverOnAvgInViewLineChart = (lineChartData) => {
    const date = lineChartData.data ? lineChartData.data.origDate : lineChartData.origDate;
    const legends = [...generatedPageViewLegends];
    avgInViewProducts.forEach((product) => {
      let value = 0;
      let changeRate = 0;
      const productDetails = details[selectedInterval][date] ? details[selectedInterval][date] : null;
      if (productDetails) {
        value = productDetails[product.id] ? productDetails[product.id] : 0;
        changeRate = productDetails[`${product.id}RateChange`] ? productDetails[`${product.id}RateChange`] : 0;
      }

      const legendIndex = generatedPageViewLegends.map((product) => product.id).indexOf(product.id);
      legends[legendIndex] = {
        ...legends[legendIndex],
        value,
        changeRate,
      };
    });
    setGeneratedPageViewLegends(legends);
    setSelectedPeriodX(`${lineChartData.data ? lineChartData.data.formattedDate : lineChartData.formattedDate}`);
    setShowGeneratedPageView(false);
  };

  const handleMouseLeave = () => {
    setShowGeneratedPageView(true);
    setSelectedPeriodX('');
  };

  const totalNewPageViewsBarChart = {
    indexBy: 'date',
    data: isGeneratedPageViewSelected ? transformGeneratedPageViewLineChartData() : transformTotalEmbedLoadBarChartData(),
    keys: getBarChartKeys(),
    colors: getBarChartColors(),
    legends: isGeneratedPageViewSelected ? renderAvgInViewLineChartLegends() : renderTotalNewPageViewsBarChartLegends(),
  };

  const handleSelectReport = (report) => {
    loadReport(true);
    setSelectedReport(report);
    timerRef.current = setTimeout(() => loadReport(false), 1500);
  };

  const cardtoggles = (
    <CardToggles>
      <CardToggle
        label={GENERATED_PAGE_VIEW_TITLE}
        compareType={changeType}
        metricType={'trending'}
        percentChange={{
          value: overview?.period ? formatNumber(overview.period.pageViewPercentageRateChange, 'percent') : 0,
          direction: overview?.period ? getChangeRateDirection(overview.period.pageViewPercentageRateChange) : 'up',
        }}
        selected={selectedReport === 'generated-page-view'}
        handleClick={() => handleSelectReport('generated-page-view')}
        value={overview?.period ? formatNumber(overview.period.pageViewPercentage, 'percent') : ''}
      />
      <CardToggle
        label={TOTAL_NEW_PAGE_VIEWS_TITLE}
        compareType={changeType}
        metricType={'trending'}
        percentChange={{
          value: overview?.period ? formatNumber(overview.period.totalPageViewsRateChange, 'percent') : 0,
          direction: overview?.period ? getChangeRateDirection(overview.period.totalPageViewsRateChange) : 'up',
        }}
        selected={selectedReport === 'total-page-views'}
        handleClick={() => handleSelectReport('total-page-views')}
        value={overview?.period ? formatNumber(overview.period.totalPageViews) : ''}
      />
    </CardToggles>
  );

  const formattedData = transformGeneratedPageViewLineChartData();
  const hasSingleData = () => {
    let singleData = false;
    formattedData.forEach((item) => {
      if (item.data.length === 1) singleData = true;
    });
    return singleData;
  };

  //----------------------------------------main functions for line chart and barchart-----------------------------------------
  const barchart = (
    <BarChart
      indexBy={'date'}
      colors={totalNewPageViewsBarChart.colors}
      keys={totalNewPageViewsBarChart.keys}
      data={totalNewPageViewsBarChart.data}
      period={selectedInterval}
      loading={status === 'pending' || isLoadingReport ? true : false}
      onHover={(data) => handleHoverOnEmbedLoadBarChart(data)}
      onMouseLeave={handleMouseLeave}
      reset={showGeneratedPageView}
      zAxisId={isGeneratedPageViewSelected ? 'avgCommentsPerUniqueUser' : null}
      zAxisColor={chartZAxisColor}
    />
  );

  const lineChart = (
    <LineChart
      category={'product'}
      enablePoints={hasSingleData()}
      data={formattedData}
      onHover={handleHoverOnAvgInViewLineChart}
      onMouseLeave={handleMouseLeave}
      reset={showGeneratedPageView}
      yType={'generated-page-view'}
      useDashedLines={true}
      period={selectedInterval}
      isPercentageData={isPercentageMetric('VIEWABILITY')}
    />
  );

  const detailtable = (
    <DetailsTable
      period={selectedInterval}
      tableType={'commenting'}
      products={isGeneratedPageViewSelected ? avgInViewProducts : totalNewPageViewsProduct}
      data={details ? details[selectedInterval] : null}
      csvFileName={isGeneratedPageViewSelected ? GENERATED_PAGE_VIEW_TITLE : TOTAL_NEW_PAGE_VIEWS_TITLE}
      dataType={isGeneratedPageViewSelected ? 'percent' : 'number'}
    />
  );

  /**
   *
   * @returns if data is there or not to display on commenting page
   */
  const isNoData = () => {
    return (
      status === 'rejected' ||
      (status === 'fulfilled' && !overview?.period?.totalPageViews) ||
      (status === 'fulfilled' && Object.keys(overview).length === 0)
    );
  };

  return (
    <div name={'pageviewreporttrendingnowv2'}>
      <ChartReportSection
        legendTitle={selectedPeriodX}
        title={`${GENERATED_PAGE_VIEW_TITLE}/${TOTAL_NEW_PAGE_VIEWS_TITLE}`}
        tooltipContent={
          <div className={'tip-content'}>
            <div className={'tip-title'}>
              <h4>{isGeneratedPageViewSelected ? GENERATED_PAGE_VIEW_TITLE : TOTAL_NEW_PAGE_VIEWS_TITLE}</h4>
            </div>
            <div className={'tip-desc'}>
              <p>
                {isGeneratedPageViewSelected
                  ? 'The Generated Page View % is the number of Embed In-view events divided by Total Embed load events.'
                  : 'The Total New Page Views metric indicates the number of times the trending now unit has been loaded on the users page.'}
              </p>
            </div>
          </div>
        }
        cardtoggles={cardtoggles}
        chart={isGeneratedPageViewSelected ? lineChart : barchart}
        note={''}
        // loading={status === 'pending' ? true : false}
        noData={isNoData()}
        enabledPeriods={enabledRollups}
        chartLegend={totalNewPageViewsBarChart.legends}
        handleSelectPeriod={(period) => {
          setSelectedInterval(period);
          handleMouseLeave();
        }}
        selectedPeriod={selectedInterval}
        detailTable={detailtable}
      />
    </div>
  );
});

/* ++++++++++ --------------- EXPORTS --------------- ++++++++++ */
export default PageViewReport;
